/**
 * Fetch adapter for the Digital Indian Market backend.
 *
 * The backend is mounted at `/api/v1` and wraps every response in either
 * `{ success: true, data }` or `{ success: false, error }`. Keep that detail
 * here so feature code only ever receives data or `{ code, message }` errors.
 */

const BACKEND_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const API_BASE = BACKEND_URL.endsWith("/api/v1") ? BACKEND_URL : `${BACKEND_URL}/api/v1`;

const VENDOR_SESSION_KEY = "business.session";
const ADMIN_SESSION_KEY = "business.admin_session";

/**
 * Raised when the API retires a token that is still in storage.
 *
 * The auth providers listen for it. They have to be told rather than find out:
 * clearing localStorage re-renders nothing, so without this the user keeps
 * looking at a dashboard whose every request is now failing.
 */
export const SESSION_EXPIRED_EVENT = "business:session-expired";

/**
 * Subscribes to forced sign-outs for one scope. Returns the unsubscribe, so a
 * `useEffect` can return it directly.
 *
 * @param {"vendor"|"admin"} scope
 * @param {(detail: { scope: string, reason: string }) => void} handler
 */
export function onSessionExpired(scope, handler) {
  const listener = (event) => {
    if (event.detail?.scope === scope) handler(event.detail);
  };
  window.addEventListener(SESSION_EXPIRED_EVENT, listener);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, listener);
}

function readSessionToken(sessionKey) {
  try {
    return JSON.parse(localStorage.getItem(sessionKey) || "null")?.token || null;
  } catch {
    return null;
  }
}

function toApiError(error, fallbackCode, fallbackMessage) {
  const normalised = new Error(error?.message || fallbackMessage);
  normalised.code = error?.code || fallbackCode;
  normalised.details = error?.details;
  return normalised;
}

async function parseResponse(response) {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    if (!response.ok) throw toApiError(null, response.status, response.statusText || "Request failed");
    return text;
  }
}

/**
 * Thin fetch wrapper with session-aware bearer authentication.
 *
 * `anonymous` suppresses the bearer header for endpoints that must not be
 * treated as an authenticated call — the password-reset flow, where a stale
 * token from a previous session has no business riding along.
 */
async function request(method, path, body, { admin = false, anonymous = false, headers: extraHeaders } = {}) {
  const headers = { Accept: "application/json", ...extraHeaders };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const sessionKey = admin ? ADMIN_SESSION_KEY : VENDOR_SESSION_KEY;
  const token = anonymous ? null : readSessionToken(sessionKey);
  if (token) headers.Authorization = `Bearer ${token}`;

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "include",
      cache: path === "/auth/me" ? "no-store" : undefined,
    });
  } catch {
    throw toApiError(null, "NETWORK_ERROR", "Unable to reach the Digital Indian Market API. Check the backend URL and CORS settings.");
  }

  const payload = await parseResponse(response);
  const error = payload?.error;
  if (!response.ok || payload?.success === false) {
    // A password reset retires every token issued before it. There is nothing
    // to retry and nothing worth keeping, and any request can be the one that
    // discovers it — so it is handled here rather than in each caller.
    if (response.status === 401 && error?.code === "PASSWORD_CHANGED" && !anonymous) {
      localStorage.removeItem(sessionKey);
      window.dispatchEvent(
        new CustomEvent(SESSION_EXPIRED_EVENT, {
          detail: { scope: admin ? "admin" : "vendor", reason: "password-changed" },
        }),
      );
    }
    throw toApiError(error, response.status, response.statusText || "Request failed");
  }

  return payload?.success === true ? payload.data : payload;
}

const GET = (path, options) => request("GET", path, undefined, options);
const POST = (path, body, options) => request("POST", path, body, options);
const PATCH = (path, body, options) => request("PATCH", path, body, options);
const PUT = (path, body, options) => request("PUT", path, body, options);
const DELETE = (path, options) => request("DELETE", path, undefined, options);

/* ---------- Auth -------------------------------------------------------- */
// The backend has one credential endpoint. The session boundary validates the
// returned role before exposing it as a vendor or admin login.
export const loginVendor = (body) => POST("/auth/login", body);
export const registerVendor = (body) => POST("/auth/register", body);
export const logoutVendor = () => POST("/auth/logout");
export const getCurrentVendor = () => GET("/auth/me");
export const loginAdmin = (body) => POST("/auth/login", body, { admin: true });
export const logoutAdmin = () => POST("/auth/logout", undefined, { admin: true });
export const getCurrentAdmin = () => GET("/auth/me", { admin: true });

/** ---------- Vendor dashboard ----------------------------------------- */
export const getDashboardSummary = ({ from, to, storefrontId } = {}) => {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (storefrontId) params.set("storefrontId", storefrontId);
  const query = params.toString();
  return GET(`/dashboard/summary${query ? `?${query}` : ""}`);
};

/* ---------- Password reset ---------------------------------------------- */
// Three steps, all public, all deliberately anonymous. One flow serves vendors
// and admins alike: the server resolves the account from the address.
// See backend `FORGOT_PASSWORD_CONTRACT.md`.

/**
 * Step 1 — emails a 6-digit code.
 *
 * Resolves the same way for every address, registered or not, because the
 * server answers identically either way: telling them apart would let anyone
 * discover who holds an account. **Never branch on the result.**
 */
export const forgotPassword = ({ email }) => POST("/auth/forgot-password", { email }, { anonymous: true });

/**
 * Step 2 — trades the code for a single-use reset token.
 *
 * `otp` stays a string: the codes are zero-padded, and `004821` coerced to a
 * number is `4821`, which matches nothing. Rejections are all `INVALID_RESET_OTP`
 * — a wrong code, an unknown address, an expired request and an exhausted one
 * are not distinguished.
 */
export const verifyResetOtp = ({ email, otp }) =>
  POST("/auth/verify-reset-otp", { email, otp: String(otp) }, { anonymous: true });

/**
 * Step 3 — sets the password and signs the account out everywhere.
 *
 * Returns no token by design: the user signs in again with the password they
 * just chose, which proves it is what they think it is.
 */
export const resetPassword = ({ resetToken, password }) =>
  POST("/auth/reset-password", { resetToken, password }, { anonymous: true });

/* ---------- Platform admin access --------------------------------------- */
export const listPlatformAdmins = () => GET("/admin/admin-users", { admin: true });
export const createPlatformAdmin = (body) => POST("/admin/admin-users", body, { admin: true });
export const updatePlatformAdmin = (id, body) => PATCH(`/admin/admin-users/${id}`, body, { admin: true });

/* ---------- Platform admin businesses ---------------------------------- */
export const listAdminBusinesses = ({ search, status, plan, page = 1, limit = 25 } = {}) => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) params.set("search", search);
  if (status && status !== "all") params.set("status", status);
  if (plan && plan !== "all") params.set("plan", plan);
  return GET(`/admin/restaurants?${params}`, { admin: true });
};
export const getAdminBusiness = (id) => GET(`/admin/restaurants/${encodeURIComponent(id)}`, { admin: true });
export const updateAdminBusinessStatus = (id, status) => PATCH(`/admin/restaurants/${encodeURIComponent(id)}/status`, { status }, { admin: true });
export const updateAdminBusinessSubscription = (id, body) => PATCH(`/admin/restaurants/${encodeURIComponent(id)}/subscription`, body, { admin: true });
export const updateAdminBusinessCapabilities = (id, body) => PATCH(`/admin/restaurants/${encodeURIComponent(id)}/capabilities`, body, { admin: true });
export const deleteAdminBusiness = (id) => DELETE(`/admin/restaurants/${encodeURIComponent(id)}`, { admin: true });
export const getAdminOverview = () => GET("/admin/restaurants/overview", { admin: true });
export const listAdminCustomerRevenue = ({ page = 1, limit = 20 } = {}) => GET(`/admin/restaurants/customer-revenue?page=${page}&limit=${limit}`, { admin: true });
export const createAdminBusiness = (body) => POST("/admin/restaurants", body, { admin: true });
export const getAdminPersonaMatrix = () => GET("/admin/persona-matrix", { admin: true });
export const saveAdminPersonaMatrix = (matrix) => PUT("/admin/persona-matrix", matrix, { admin: true });
export const resetAdminPersonaMatrix = () => DELETE("/admin/persona-matrix", { admin: true });
export const createAdminChangeRequest = (body) => POST("/admin/change-requests", body, { admin: true });
export const listAdminChangeRequests = (status) => GET(`/admin/change-requests${status ? `?status=${encodeURIComponent(status)}` : ""}`, { admin: true });
export const reviewAdminChangeRequest = (id, body) => POST(`/admin/change-requests/${encodeURIComponent(id)}/review`, body, { admin: true });
export const listAdminOrders = ({ q, status, date, page = 1, limit = 100 } = {}) => { const params = new URLSearchParams({ page: String(page), limit: String(limit) }); if (q) params.set("q", q); if (status && status !== "all") params.set("status", status); if (date) params.set("date", date); return GET(`/admin/orders?${params}`, { admin: true }); };
export const listAdminSubscriptions = ({ status, plan } = {}) => { const params = new URLSearchParams(); if (status) params.set("status", status); if (plan) params.set("plan", plan); return GET(`/admin/restaurants/subscriptions${params.toString() ? `?${params}` : ""}`, { admin: true }); };
export const listAdminSupportTickets = () => GET("/admin/support/tickets", { admin: true });
export const updateAdminSupportTicketStatus = (id, status) => PATCH(`/admin/support/tickets/${encodeURIComponent(id)}/status`, { status }, { admin: true });

/* ---------- Vendor onboarding ------------------------------------------- */
export const getOwnRestaurant = () => GET("/restaurant");
export const updateOwnRestaurant = (body) => PATCH("/restaurant", body);
export const getOwnStorefront = () => GET("/storefront");
export const checkOwnStorefrontSlug = (slug) => GET(`/storefront/slug-availability?${new URLSearchParams({ slug })}`);
export const updateOwnStorefront = (body) => PATCH("/storefront", body);
export const listVendorTables = ({ storefrontId } = {}) => {
  const params = new URLSearchParams();
  if (storefrontId) params.set("storefrontId", storefrontId);
  return GET(`/tables${params.toString() ? `?${params}` : ""}`);
};
export const resizeVendorTables = ({ storefrontId, count }) => PUT("/tables", { storefrontId, count });

/* ---------- Vendor catalogue -------------------------------------------- */
export const createCategory = (body) => POST("/categories", body);
export const updateCategory = (id, body) => PATCH(`/categories/${id}`, body);
export const deleteCategory = (id) => DELETE(`/categories/${id}`);
export const createMenuItem = (body) => POST("/menu-items", body);
export const updateMenuItem = (id, body) => PATCH(`/menu-items/${id}`, body);
export const deleteMenuItem = (id) => DELETE(`/menu-items/${id}`);
export const listVendorMenuItems = ({ storefrontId, state = "available" } = {}) => {
  const params = new URLSearchParams({ limit: "100" });
  if (storefrontId) params.set("storefrontId", storefrontId);
  if (state) params.set("state", state);
  return GET(`/menu-items?${params}`);
};

/* ---------- Vendor orders ----------------------------------------------- */
export const listVendorOrders = ({ storefrontId, status, source, tableId, q, date, foodType } = {}) => {
  const params = new URLSearchParams();
  if (storefrontId && storefrontId !== "all") params.set("storefrontId", storefrontId);
  if (status && status !== "all") params.set("status", status);
  if (source) params.set("source", source);
  if (tableId) params.set("tableId", tableId);
  if (q) params.set("q", q);
  if (date) params.set("date", date);
  if (foodType) params.set("foodType", foodType);
  params.set("limit", "100");
  return GET(`/orders?${params}`);
};
export const getVendorOrder = (id) => GET(`/orders/${id}`);
export const createVendorOrder = (body) => POST("/orders", body);
export const updateVendorOrderStatus = (id, status) => PATCH(`/orders/${id}/status`, { status });
export const addVendorOrderItems = (id, items) => POST(`/orders/${id}/items`, { items });
export const closeVendorOrderTable = (id) => POST(`/orders/${id}/close-table`);
export const listVendorServiceRequests = ({ storefrontId, status } = {}) => {
  const params = new URLSearchParams();
  if (storefrontId && storefrontId !== "all") params.set("storefrontId", storefrontId);
  if (status) params.set("status", status);
  return GET(`/service-requests${params.toString() ? `?${params}` : ""}`);
};
export const updateVendorServiceRequestStatus = (id, status) => PATCH(`/service-requests/${id}`, { status });
export const listVendorSupportTickets = () => GET("/support/tickets");
export const createVendorSupportTicket = (body) => POST("/support/tickets", body);

/* ---------- Public customer storefront ---------------------------------- */
// Deliberately anonymous: a vendor previewing their own storefront must take
// the same network path as a diner, and a bearer header here only earns a
// CORS preflight the CDN's cached public routes aren't set up to answer —
// these endpoints resolve the tenant from the slug and ignore auth entirely.
export const getPublicStorefront = (slug, { tableId } = {}) => {
  const params = new URLSearchParams();
  if (tableId) params.set("table", tableId);
  return GET(`/public/${encodeURIComponent(slug)}${params.toString() ? `?${params}` : ""}`, { anonymous: true });
};
export const getPublicStorefrontMenu = (slug, { tableId, foodType } = {}) => {
  const params = new URLSearchParams();
  if (tableId) params.set("table", tableId);
  if (foodType) params.set("foodType", foodType);
  return GET(`/public/${encodeURIComponent(slug)}/menu${params.toString() ? `?${params}` : ""}`, { anonymous: true });
};
export const createPublicOrder = (slug, body, { tableId } = {}) => {
  const params = new URLSearchParams();
  if (tableId) params.set("table", tableId);
  return POST(`/public/${encodeURIComponent(slug)}/orders${params.toString() ? `?${params}` : ""}`, body, { anonymous: true });
};
export const getPublicOrder = (id) => GET(`/public/orders/${encodeURIComponent(id)}`, { anonymous: true });
const publicSessionOptions = (sessionId) => ({
  anonymous: true,
  ...(sessionId ? { headers: { "x-customer-session": sessionId } } : {}),
});
export const createPublicCustomerSession = (slug, sessionId) => POST(`/public/${encodeURIComponent(slug)}/session`, undefined, publicSessionOptions(sessionId));
export const savePublicCustomerSession = (slug, sessionId, body) => PATCH(`/public/${encodeURIComponent(slug)}/session`, body, publicSessionOptions(sessionId));
export const activatePublicCustomerSession = (slug, sessionId, body) => POST(`/public/${encodeURIComponent(slug)}/session/activate`, body, publicSessionOptions(sessionId));
export const rotatePublicCustomerSession = (slug) => POST(`/public/${encodeURIComponent(slug)}/session/rotate`, undefined, { anonymous: true });
export const listPublicCustomerOrders = (slug, sessionId) => GET(`/public/${encodeURIComponent(slug)}/session/orders`, publicSessionOptions(sessionId));
export const listPublicCustomerServiceRequests = (slug, sessionId) => GET(`/public/${encodeURIComponent(slug)}/session/requests`, publicSessionOptions(sessionId));
export const createPublicServiceRequest = (slug, sessionId, body) => POST(`/public/${encodeURIComponent(slug)}/service-requests`, body, publicSessionOptions(sessionId));

/* ---------- Existing compatibility exports ----------------------------- */
// These remain at their legacy paths until their corresponding client slices
// are migrated to the backend resource contract. Auth is deliberately the
// first server-backed slice.
export const listVendors = ({ q, type } = {}) => {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (type) params.set("type", type);
  return GET(`/vendors${params.toString() ? `?${params}` : ""}`);
};
export const getVendor = (id) => GET(`/vendors/${id}`);
export const getVendorBySlug = (slug) => GET(`/vendors/by-slug/${slug}`);
export const updateVendor = (id, patch) => PATCH(`/vendors/${id}`, patch);
export const deleteVendor = (id) => DELETE(`/vendors/${id}`, { admin: true });
export const checkSlug = (slug, exceptId) => {
  const params = new URLSearchParams({ slug });
  if (exceptId) params.set("exceptId", exceptId);
  return GET(`/vendors/check-slug?${params}`);
};
export const listOrders = ({ vendorId, status, q } = {}) => {
  const params = new URLSearchParams();
  if (vendorId) params.set("vendorId", vendorId);
  if (status) params.set("status", status);
  if (q) params.set("q", q);
  return GET(`/orders${params.toString() ? `?${params}` : ""}`);
};
export const createOrder = (body) => POST("/orders", body);
export const updateOrderStatus = (orderId, status) => PATCH(`/orders/${orderId}`, { status });
export const listBookings = ({ vendorId, status, q } = {}) => {
  const params = new URLSearchParams();
  if (vendorId) params.set("vendorId", vendorId);
  if (status) params.set("status", status);
  if (q) params.set("q", q);
  return GET(`/bookings${params.toString() ? `?${params}` : ""}`);
};
export const createBooking = (body) => POST("/bookings", body);
export const updateBookingStatus = (bookingId, status) => PATCH(`/bookings/${bookingId}`, { status });
export const getSlots = (vendorId, dateISO) => GET(`/bookings/slots?vendorId=${vendorId}&date=${dateISO}`);

export { request, toApiError };
